/**
 * Service bundles (tracker item 85). Mirrors backend
 * modules/service-bundles/service-bundles.types.ts and
 * modules/bundle-enrollments/*.types.ts. Money is a decimal string.
 */

// ---------------------------------------------------------------- bundles

export type BundleSkipReason = 'ITEM_INACTIVE' | 'ITEM_DELETED';

export interface BundleComponent {
  id: string;
  sortOrder: number;
  itemId: string;
  itemName: string;
  itemType: string;
  /** False when the item was deleted (or is a system item): adding the bundle skips this visit */
  itemAvailable: boolean;
  quantity: string;
  daysAfterPrevious: number;
  notes: string | null;
}

export interface ServiceBundle {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  portalRequestable: boolean;
  autoRenewDefault: boolean;
  components: BundleComponent[];
  componentCount: number;
  /** Days from the first visit to the last (sum of the gaps after the first component) */
  visitSpanDays: number;
  createdAt: string;
  updatedAt: string;
}

export interface BundleComponentInput {
  itemId: string;
  quantity: string | number;
  daysAfterPrevious: number;
  notes?: string | null;
}

export interface CreateServiceBundleRequest {
  name: string;
  description?: string | null;
  isActive?: boolean;
  portalRequestable?: boolean;
  autoRenewDefault?: boolean;
  components: BundleComponentInput[];
}

/** PUT: header fields are kept when omitted; the components always replace the old list. */
export interface UpdateServiceBundleRequest extends Partial<Omit<CreateServiceBundleRequest, 'components'>> {
  components: BundleComponentInput[];
}

export interface ServiceBundleListFilters {
  isActive?: boolean;
  portalRequestable?: boolean;
  search?: string;
}

export interface BundlePreviewVisit {
  step: number;
  componentId: string;
  itemId: string;
  itemName: string;
  quantity: string;
  nominalDate: string;
  /** The date the visit job gets (after the work-day shift) */
  plannedDate: string;
  daysAfterPrevious: number;
  unitPrice: string;
  priceSource: string;
  taxRate: string;
  lineTotal: string;
  lineTax: string;
  lineAmount: string;
  notes: string | null;
}

export interface BundleSkippedComponent {
  componentId: string;
  sortOrder: number;
  itemId: string;
  itemName: string;
  reason: BundleSkipReason;
}

export interface BundlePreview {
  bundleId: string;
  bundleName: string;
  propertyId: string;
  startDate: string;
  stepCount: number;
  visits: BundlePreviewVisit[];
  skipped: BundleSkippedComponent[];
  subtotal: string;
  totalTax: string;
  total: string;
}

// ---------------------------------------------------------------- enrollments

export type EnrollmentStatus = 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'PENDING_APPROVAL';

/** Where a renewal step's price came from (never the chart or catalog). */
export type RenewalPriceSource = 'RENEWAL_PRICE' | 'LAST_CHARGED' | 'ORIGINAL';

export interface EnrollmentStepJob {
  id: string;
  status: string;
  scheduledDate: string | null;
  completedDate: string | null;
  assignedTo: string | null;
  invoiceId: string | null;
}

export interface EnrollmentStep {
  step: number;
  itemId: string;
  itemName: string;
  quantity: string;
  daysAfterPrevious: number;
  plannedDate: string;
  /** The price fixed when the bundle was added */
  unitPrice: string;
  /** The staff-set renewal price; null = follows the price paid today */
  renewalUnitPrice: string | null;
  effectiveRenewalPrice: string;
  renewalPriceSource: RenewalPriceSource;
  /** The visit job's current unit price; null without a job */
  jobUnitPrice: string | null;
  lineTotal: string;
  lineTax: string | null;
  jobId: string | null;
  job: EnrollmentStepJob | null;
}

export interface EnrollmentProgress {
  total: number;
  completed: number;
  cancelled: number;
  remaining: number;
}

export interface EnrollmentPropertyRef {
  id: string;
  address: string;
  city: string;
  state: string;
}

export interface BundleEnrollment {
  id: string;
  bundleId: string | null;
  bundleName: string;
  customerId: string;
  customer: { id: string; name: string };
  propertyId: string;
  property: EnrollmentPropertyRef;
  startDate: string;
  /** The last planned visit */
  endDate: string;
  status: EnrollmentStatus;
  stepCount: number;
  autoRenew: boolean;
  renewedFromId: string | null;
  renewedToId: string | null;
  createdBy: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  progress: EnrollmentProgress;
  /** Earliest scheduled date among visits not yet started or finished */
  nextVisitDate: string | null;
  /** Sum of the visits' pre-tax amounts (cancelled visits excluded) */
  total: string;
  steps: EnrollmentStep[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatedEnrollment extends BundleEnrollment {
  skipped: BundleSkippedComponent[];
}

export interface EnrollmentListFilters {
  status?: EnrollmentStatus;
  customerId?: string;
  propertyId?: string;
  bundleId?: string;
  autoRenew?: boolean;
  page?: number;
  limit?: number;
}

export interface EnrollmentPage {
  data: BundleEnrollment[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface AddBundleRequest {
  bundleId: string;
  startDate: string;
  assignedTo?: string | null;
  routeId?: string | null;
  /** Omitted = the bundle's autoRenewDefault */
  autoRenew?: boolean;
}

// ---------------------------------------------------------------- renewals

export interface RenewalQuoteStep {
  step: number;
  itemId: string;
  itemName: string;
  quantity: string;
  daysAfterPrevious: number;
  plannedDate: string;
  /** The effective renewal price */
  unitPrice: string;
  priceSource: RenewalPriceSource;
  /** What the customer pays today for this visit (null without a job) */
  currentUnitPrice: string | null;
  renewalUnitPrice: string | null;
  taxRate: string;
  lineTotal: string;
  lineTax: string;
  lineAmount: string;
}

export interface RenewalQuote {
  kind: 'RENEWAL_PREVIEW' | 'PENDING_RENEWAL' | 'PENDING_REQUEST';
  enrollmentId: string;
  bundleId: string | null;
  bundleName: string;
  customer: { id: string; name: string };
  property: EnrollmentPropertyRef;
  startDate: string;
  autoRenew: boolean;
  renewal: { id: string; status: EnrollmentStatus } | null;
  steps: RenewalQuoteStep[];
  skipped: { step: number; itemId: string; itemName: string; reason: string }[];
  subtotal: string;
  estimatedTax: string;
  estimatedTotal: string;
}

export interface RenewalCreateResult {
  /** false when the enrollment already had a renewal (that one is returned) */
  created: boolean;
  renewal: BundleEnrollment;
}

export interface BulkRenewalRowResult {
  enrollmentId: string;
  success: boolean;
  created?: boolean;
  renewalId?: string;
  error?: { code: string; message: string };
}

export interface BulkRenewalResult {
  total: number;
  succeeded: number;
  failed: number;
  results: BulkRenewalRowResult[];
}

export type RenewalStatus = 'NONE' | 'PENDING_APPROVAL' | 'ACTIVE' | 'COMPLETED';

export interface RenewalRow {
  id: string;
  bundleId: string | null;
  bundleName: string;
  customer: { id: string; name: string };
  property: EnrollmentPropertyRef;
  startDate: string;
  endDate: string;
  /** Calendar days from today to endDate; negative once it has passed */
  daysLeft: number;
  status: EnrollmentStatus;
  autoRenew: boolean;
  stepCount: number;
  renewalStatus: RenewalStatus;
  renewalId: string | null;
  quoteTotal: string;
}

export interface RenewalListFilters {
  bundleId?: string;
  year?: number;
  daysUntilEnd?: number;
  hasRenewal?: boolean;
  autoRenew?: boolean;
  status?: EnrollmentStatus;
  customerId?: string;
  propertyId?: string;
  page?: number;
  limit?: number;
}

export interface RenewalPage {
  data: RenewalRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface ApproveEnrollmentRequest {
  startDate?: string;
  assignedTo?: string | null;
  routeId?: string | null;
  autoRenew?: boolean;
  /** The pre-tax total the approver was shown */
  expectedTotal?: string;
  /** Confirms a total that changed since expectedTotal was shown */
  confirmPriceChange?: boolean;
}

export interface DeclineEnrollmentRequest {
  reason?: string;
}

/** What the renewal price prompt asks (decision 15). */
export type RenewalPriceAction = 'UPDATE' | 'KEEP';

export interface StepPriceRequest {
  unitPrice: string | number;
  renewalPriceAction?: RenewalPriceAction;
}

/** 409 RENEWAL_PRICE_CHANGED details: the approve screen shows old versus new and asks for confirmation. */
export interface RenewalPriceChangedDetails {
  expectedTotal: string;
  currentTotal: string;
  steps: { step: number; itemName: string; from: string; to: string }[];
}

/** 400 RENEWAL_PRICE_CHOICE_REQUIRED details: the client shows the prompt and resends with a choice. */
export interface RenewalPriceChoiceDetails {
  enrollmentId: string;
  step: number;
  jobId: string;
  oldUnitPrice: string;
  newUnitPrice: string;
  /** The staff-set renewal price, or null when it follows today's price */
  renewalUnitPrice: string | null;
  currentRenewalPrice: string;
}

export const BUNDLE_ERROR_CODES = {
  renewalPriceChoiceRequired: 'RENEWAL_PRICE_CHOICE_REQUIRED',
  renewalPriceChanged: 'RENEWAL_PRICE_CHANGED',
} as const;

// ---------------------------------------------------------------- intake queue (staff Requests inbox)

export type PendingEnrollmentType = 'BUNDLE_REQUEST' | 'BUNDLE_RENEWAL';

/** An entry of GET /enrollments/pending: a bundle request or renewal waiting for approval. */
export interface PendingEnrollmentEntry {
  type: PendingEnrollmentType;
  /** The enrollment id */
  id: string;
  /** The staff screen that decides it: /enrollments/:id */
  link: string;
  bundleName: string;
  customer: { id: string; name: string };
  property: EnrollmentPropertyRef;
  startDate: string;
  endDate: string;
  stepCount: number;
  autoRenew: boolean;
  /** Pre-tax program total at the current quote */
  total: string;
  renewedFromId: string | null;
  createdBy: string;
  createdAt: string;
}

export interface PendingEnrollmentFilters {
  type?: PendingEnrollmentType;
  page?: number;
  limit?: number;
}

export interface PendingEnrollmentPage {
  data: PendingEnrollmentEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

// ---------------------------------------------------------------- customer portal

/** GET /portal/requestable-bundles: a bundle the customer may request, with its visits and an estimated total. */
export interface PortalRequestableBundle {
  id: string;
  name: string;
  description: string | null;
  /** The request form pre-sets its auto-renew checkbox with this */
  autoRenewDefault: boolean;
  visitCount: number;
  /** Days from the first visit to the last */
  visitSpanDays: number;
  visits: {
    step: number;
    itemId: string;
    itemName: string;
    quantity: string;
    /** Days after the first visit */
    offsetDays: number;
    daysAfterPrevious: number;
    /** Pre-tax price of the visit; null = could not be priced (unlinked login, or a chart that needs the property size) */
    estimatedPrice: string | null;
  }[];
  /** The customer's property the prices are for; null = no prices */
  pricedForPropertyId: string | null;
  estimatedSubtotal: string | null;
  estimatedTax: string | null;
  estimatedTotal: string | null;
}

export interface PortalBundleRequestInput {
  bundleId: string;
  propertyId: string;
  startDate: string;
  /** Omitted = the bundle's default */
  autoRenew?: boolean;
}

export type PortalVisitStatus = 'REQUESTED' | 'PLANNED' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

/** One visit as the customer sees it: no technician, cost or invoice data. */
export interface PortalEnrollmentStep {
  step: number;
  itemName: string;
  quantity: string;
  plannedDate: string;
  /** The job's date once scheduled, else the planned date */
  scheduledDate: string;
  completedDate: string | null;
  status: PortalVisitStatus;
  unitPrice: string;
  /** Pre-tax amount of the visit */
  lineTotal: string;
  /** The visit's job (the portal's /jobs/:id) once it exists */
  jobId: string | null;
}

/** GET /portal/enrollments[/:id] */
export interface PortalEnrollment {
  id: string;
  bundleName: string;
  property: EnrollmentPropertyRef;
  startDate: string;
  endDate: string;
  status: EnrollmentStatus;
  autoRenew: boolean;
  stepCount: number;
  progress: EnrollmentProgress;
  nextVisitDate: string | null;
  total: string;
  steps: PortalEnrollmentStep[];
  createdAt: string;
}

export interface PortalEnrollmentPage {
  data: PortalEnrollment[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface PortalEnrollmentListQuery {
  status?: EnrollmentStatus;
  page?: number;
  limit?: number;
}

// ---------------------------------------------------------------- alert routing

export type BundleAlertType = 'BUNDLE_RENEWAL_PENDING' | 'BUNDLE_REQUEST_PENDING' | 'BUNDLE_RENEWAL_BLOCKED';
export type BundleAlertRole = 'ADMIN' | 'OFFICE_MANAGER';

export interface BundleAlertRule {
  roles: BundleAlertRole[];
  userIds: string[];
  everyone: boolean;
  enrollmentCreator: boolean;
}

export type BundleAlertRouting = Record<BundleAlertType, BundleAlertRule>;

export interface BundleAlertWarning {
  type: BundleAlertType;
  code: 'NO_ACTIVE_RECIPIENTS';
  message: string;
}

export interface BundleAlertUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
}

export interface BundleAlertRoutingResponse {
  routing: BundleAlertRouting;
  warnings: BundleAlertWarning[];
  eligibleUsers: BundleAlertUser[];
}

/** The enrollment fields every job response carries (null outside an enrollment). */
export interface JobEnrollmentRef {
  enrollmentId?: string | null;
  enrollmentStep?: number | null;
  enrollmentStepCount?: number | null;
  bundleName?: string | null;
}
